import { invalidateMatterAndSearchQueries } from "@/lib/data/react-query/search/invalidation";
import { FileReferencesSection } from "@/components/features/matter/file-repo/FileReferencesSection";
import { SelectTreatmentAppointmentsFormField } from "@/components/features/matter/medical-treatments/SelectTreatmentAppointmentsFormField";
import {
  ActionConfirmationModal,
  AppModal,
  Button,
  DateFormField,
  Form,
  FormSubmitButton,
  Separator,
  SimpleSelectFormField,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import { useMedicalTreatmentViewModel } from "@/hooks/matter-view-models/use-medical-treatment-view-model";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import {
  useCreateMedicalTreatmentAppointmentConnection,
  useDeleteMedicalTreatmentAppointmentConnection,
} from "@/lib/data/react-query";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  useCreateMedicalTreatmentRecord,
  useDeleteMedicalTreatmentRecord,
  useUpdateMedicalTreatmentRecord,
} from "@/lib/data/react-query/matters/medical-treatment-record";
import { MedicalTreatmentRecordSchema } from "@/lib/data/schemas";
import { MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS } from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  SLIDEOVER_DESCRIPTION,
  SLIDEOVER_TITLE,
} from "@/lib/data/static/slideoverStatics";
import {
  type APIResponse,
  type MedicalTreatmentRecord,
  type MatterSlideoverInitialTabsType,
} from "@/lib/types";
import { ExpenseIcon } from "@/lib/utils/iconUtils";
import { mergeFilesAndReferenceId } from "@/lib/utils/matterFile";
import { mergeInitialData } from "@/lib/utils/initialDataUtils";
import { modalFormFieldsetClassName } from "@/lib/utils/modal-classes";
import { useModals } from "@/providers/ModalProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { difference, isNil, omitBy } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

interface AddOrEditTreatmentRecordSlideoverProps {
  title: string;
  description?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  editing?: boolean;
  initialTab?: MatterSlideoverInitialTabsType;
  matterId: string;
  recordId?: string;
  treatmentId?: string;
  afterCreateFn?: (newRecord: APIResponse<MedicalTreatmentRecord>) => void;
  creatingFromAppointment?: boolean;
  initialData?: Partial<MedicalTreatmentRecord>;
}

const recordFormSchema = MedicalTreatmentRecordSchema.omit({
  connections: true,
  connectionsNextCursor: true,
  id: true,
  dateCreated: true,
  dateModified: true,
  matterId: true,
  medicalTreatmentId: true,
})
  .extend({
    status: z
      .enum(MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS)
      .or(z.literal(""))
      .refine((value) => value, {
        message: "Status is required",
      }),
    medicalTreatmentAppointmentId: z.array(z.string()).optional(),
  })
  .superRefine((data, ctx) => {
    if (["Requested", "Received"].includes(data.status)) {
      if (!data.requestDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Record request date is required",
          path: ["requestDate"],
        });
      }
    }
    if (data.status === "Received") {
      if (!data.receivedDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Record received date is required",
          path: ["receivedDate"],
        });
      }
    }
    if (data.status === "Rejected") {
      if (!data.rejectedDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Record rejected date is required",
          path: ["rejectedDate"],
        });
      }
      if (!data.rejectedReason?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Rejected reason is required",
          path: ["rejectedReason"],
        });
      }
    }
  });

const baseFormValues = {
  name: "",
  status: "Requested" as const,
  requestDate: "",
  receivedDate: "",
  rejectedDate: "",
  rejectedReason: "",
  medicalTreatmentAppointmentId: [],
};

const getCreateFormDefaults = () => ({
  ...baseFormValues,
  status: "Requested" as const,
  requestDate: dayjs().format("YYYY-MM-DD"),
});

type SelectableTreatmentRecordStatus =
  (typeof MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS)[number];

const getSelectableTreatmentRecordStatus = (
  status?: string | null,
): SelectableTreatmentRecordStatus | "" =>
  status &&
  MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS.includes(
    status as SelectableTreatmentRecordStatus,
  )
    ? (status as SelectableTreatmentRecordStatus)
    : "";

export const AddOrEditTreatmentRecordSlideover = ({
  title,
  description,
  open,
  setOpen,
  editing,
  matterId,
  recordId,
  treatmentId,
  afterCreateFn,
  creatingFromAppointment,
  initialData,
}: AddOrEditTreatmentRecordSlideoverProps) => {
  const queryClient = useQueryClient();
  const { hasActionPermission } = useUser();
  const canCreateAppointmentConnections = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentAppointmentConnection,
    ACTION_PERMISSION_VALUES.create,
  );
  const canDeleteAppointmentConnections = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentAppointmentConnection,
    ACTION_PERMISSION_VALUES.delete,
  );
  const { updateSlideoverPayload } = useModals();
  const { openExpenseModal } = useGlobalModal();
  const createRecord = useCreateMedicalTreatmentRecord({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });
  const updateRecord = useUpdateMedicalTreatmentRecord({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });
  const deleteRecord = useDeleteMedicalTreatmentRecord();
  const createMedicalTreatmentAppointmentConnection =
    useCreateMedicalTreatmentAppointmentConnection({
      suppressQueryInvalidation: true,
    });
  const deleteMedicalTreatmentAppointmentConnection =
    useDeleteMedicalTreatmentAppointmentConnection({
      suppressQueryInvalidation: true,
    });

  const isFormLoading =
    createRecord.isPending ||
    updateRecord.isPending ||
    createMedicalTreatmentAppointmentConnection.isPending ||
    deleteMedicalTreatmentAppointmentConnection.isPending;

  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch matter data
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );

  const matter = matterData.data;

  // Get the specific record if editing
  const selectedRecord = recordId
    ? matter.medicalTreatmentRecords.find((record) => record.id === recordId)
    : undefined;
  const recordInitialValues = mergeInitialData(selectedRecord, initialData);

  // Get the specific treatment
  const selectedTreatment = treatmentId
    ? matter.medicalTreatments.find((treatment) => treatment.id === treatmentId)
    : undefined;

  const completeMedicalTreatments = useMedicalTreatmentViewModel(matter);
  const completeTreatment = completeMedicalTreatments.find(
    (t) => t.id === selectedTreatment?.id,
  );

  const appointmentsWithConnections = completeTreatment?.appointments;
  const appointmentsWIthSelectedRecord = useMemo(() => {
    return appointmentsWithConnections?.filter((appointment) =>
      appointment.records.some((r) => r.id === recordInitialValues?.id),
    );
  }, [appointmentsWithConnections, recordInitialValues?.id]);

  const fileReferences = mergeFilesAndReferenceId(
    matter,
    "medicalTreatmentRecordId",
    recordInitialValues?.id,
  );

  const recordForm = useForm<z.infer<typeof recordFormSchema>>({
    resolver: zodResolver(recordFormSchema),
    values:
      editing && (recordInitialValues || appointmentsWIthSelectedRecord?.length)
        ? {
            ...baseFormValues,
            ...(recordInitialValues ?? {}),
            status: getSelectableTreatmentRecordStatus(
              recordInitialValues?.status,
            ),
            medicalTreatmentAppointmentId:
              appointmentsWIthSelectedRecord?.map(
                (appointment) => appointment.id,
              ) ?? [],
          }
        : getCreateFormDefaults(),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const isFormActuallyDirty =
    Object.keys(recordForm.formState.dirtyFields).length > 0;

  const [closeAfterSave, setCloseAfterSave] = useState(false);

  const onSubmit = async (data: z.infer<typeof recordFormSchema>) => {
    let resData;
    const contactConnectionPromises: Promise<APIResponse<unknown>>[] = [];
    const { medicalTreatmentAppointmentId: submittedAppointmentIds, ...rest } =
      data;
    const existingAppointmentIds =
      appointmentsWIthSelectedRecord?.map((appointment) => appointment.id) ??
      [];
    const appointmentsToAdd = difference(
      submittedAppointmentIds,
      existingAppointmentIds,
    );
    const appointmentsToRemove = difference(
      existingAppointmentIds,
      submittedAppointmentIds ?? [],
    );
    if (
      (appointmentsToAdd.length > 0 && !canCreateAppointmentConnections) ||
      (appointmentsToRemove.length > 0 && !canDeleteAppointmentConnections)
    ) {
      return;
    }
    if (editing && selectedRecord) {
      try {
        resData = await updateRecord.mutateAsync({
          matterId: matter.id,
          id: selectedRecord.id,
          dateModified: selectedRecord.dateModified,
          ...rest,
          requestDate: ["Requested", "Received"].includes(data.status)
            ? data.requestDate
            : null,
          receivedDate: data.status === "Received" ? data.receivedDate : null,
          rejectedDate: data.status === "Rejected" ? data.rejectedDate : null,
          rejectedReason:
            data.status === "Rejected" ? data.rejectedReason : null,
        });
        for (const appointmentId of appointmentsToAdd) {
          contactConnectionPromises.push(
            createMedicalTreatmentAppointmentConnection.mutateAsync({
              matterId: matter.id,
              medicalTreatmentRecordId: selectedRecord.id,
              medicalTreatmentAppointmentId: appointmentId,
            }),
          );
        }
        for (const appointmentId of appointmentsToRemove) {
          contactConnectionPromises.push(
            deleteMedicalTreatmentAppointmentConnection.mutateAsync({
              matterId: matter.id,
              id:
                matter.medicalTreatmentAppointmentConnections.find(
                  (r) =>
                    r.medicalTreatmentAppointmentId === appointmentId &&
                    r.medicalTreatmentRecordId === selectedRecord.id,
                )?.id ?? "",
            }),
          );
        }
        await Promise.all(contactConnectionPromises);
        if (closeAfterSave) {
          setOpen(false);
        }
        toast.success("Medical treatment record updated successfully");
        await invalidateMatterAndSearchQueries(queryClient);
      } catch (error) {
        console.error(error);
      }
    } else if (selectedTreatment) {
      try {
        const result = await createRecord.mutateAsync({
          matterId: matter.id,
          medicalTreatmentId: selectedTreatment.id,
          ...rest,
        });
        resData = result;
        if (afterCreateFn) {
          afterCreateFn(result);
        }
        for (const appointmentId of appointmentsToAdd) {
          contactConnectionPromises.push(
            createMedicalTreatmentAppointmentConnection.mutateAsync({
              matterId: matter.id,
              medicalTreatmentRecordId: result.data.id,
              medicalTreatmentAppointmentId: appointmentId,
            }),
          );
        }
        await Promise.all(contactConnectionPromises);
        updateSlideoverPayload("treatment-record", {
          title: SLIDEOVER_TITLE.TREATMENT_RECORD.UPDATE,
          description: SLIDEOVER_DESCRIPTION.TREATMENT_RECORD.UPDATE,
          editing: true,
          recordId: resData.data.id,
          treatmentId: selectedTreatment.id,
        });
        if (closeAfterSave) {
          setOpen(false);
        }
        toast.success("Medical treatment record created successfully");
        await invalidateMatterAndSearchQueries(queryClient);
      } catch (error) {
        console.error(error);
      }
    }
    if (resData && !closeAfterSave) {
      const sanitizedData = omitBy(
        resData.data,
        isNil,
      ) as MedicalTreatmentRecord;
      recordForm.reset({
        ...baseFormValues,
        ...sanitizedData,
        status: getSelectableTreatmentRecordStatus(sanitizedData.status),
        medicalTreatmentAppointmentId: appointmentsWIthSelectedRecord?.map(
          (appointment) => appointment.id,
        ),
      });
    }
  };

  useEffect(() => {
    if (!open) {
      recordForm.reset({
        ...(editing ? baseFormValues : getCreateFormDefaults()),
        medicalTreatmentAppointmentId: appointmentsWIthSelectedRecord?.map(
          (appointment) => appointment.id,
        ),
      });
    }
  }, [open, editing, recordForm, appointmentsWIthSelectedRecord]);

  const recordStatusFormValue = recordForm.watch("status");

  // Handler for status change
  const handleStatusChange = (newStatus: string) => {
    const currentRequestDate = recordForm.getValues("requestDate");
    const currentReceivedDate = recordForm.getValues("receivedDate");
    const currentRejectedDate = recordForm.getValues("rejectedDate");

    // When status changes to "Requested" or "Received", set request date to today if empty
    if (["Requested", "Received"].includes(newStatus) && !currentRequestDate) {
      recordForm.setValue("requestDate", dayjs().format("YYYY-MM-DD"), {
        shouldDirty: true,
      });
    }

    // When status changes to "Received", set received date to today if empty
    if (newStatus === "Received" && !currentReceivedDate) {
      recordForm.setValue("receivedDate", dayjs().format("YYYY-MM-DD"), {
        shouldDirty: true,
      });
    }

    // When status changes to "Rejected", set rejected date to today if empty
    if (newStatus === "Rejected" && !currentRejectedDate) {
      recordForm.setValue("rejectedDate", dayjs().format("YYYY-MM-DD"), {
        shouldDirty: true,
      });
    }
  };
  const recordFormId = `treatment-record-form-${
    recordId ?? treatmentId ?? "new"
  }`;

  return (
    <>
      <Form {...recordForm}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={title}
          description={description}
          isDirty={isFormActuallyDirty}
          loading={isFormLoading}
          size="3xl"
          bodyContained
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                {selectedRecord &&
                  hasActionPermission(
                    ACTION_PERMISSION_KEYS.matterMedicalTreatmentRecord,
                    ACTION_PERMISSION_VALUES.delete,
                  ) && (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => {
                        setIsDeleting(true);
                      }}
                    >
                      Delete
                    </Button>
                  )}
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <FormCancelButton
                  setOpen={setOpen}
                  isDirty={isFormActuallyDirty}
                />
                <FormSubmitButton
                  submitFormId={recordFormId}
                  variant="light-primary"
                  loading={isFormLoading}
                  overrideDisabled={Boolean(initialData)}
                  submitFn={() => {
                    setCloseAfterSave(false);
                  }}
                />
                <FormSubmitButton
                  submitFormId={recordFormId}
                  variant="default"
                  loading={isFormLoading}
                  overrideDisabled={Boolean(initialData)}
                  submitFn={() => {
                    setCloseAfterSave(true);
                  }}
                  title="Save and close"
                />
              </div>
            </div>
          }
        >
          {hasActionPermission(
            ACTION_PERMISSION_KEYS.matterExpense,
            ACTION_PERMISSION_VALUES.create,
          ) && (
            <div className="bg-light-primary/50 border-light-primary border-b p-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  openExpenseModal({
                    matterId: matter.id,
                    editing: false,
                    initialTab: "details",
                  });
                }}
              >
                <ExpenseIcon className="h-4 w-4" />
                Create expense
              </Button>
            </div>
          )}
          <form
            id={recordFormId}
            className="relative flex min-h-0 flex-1 flex-col overflow-hidden"
            onSubmit={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              await recordForm.handleSubmit(onSubmit)();
            }}
          >
            {isFormLoading && <FormLoadingOverlay />}
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <fieldset
                className={modalFormFieldsetClassName}
                disabled={
                  editing
                    ? !hasActionPermission(
                        ACTION_PERMISSION_KEYS.matterMedicalTreatmentRecord,
                        ACTION_PERMISSION_VALUES.update,
                      )
                    : false
                }
              >
                <div className="col-span-full grid grid-cols-subgrid">
                  {recordStatusFormValue === "Received" && (
                    <TextFormField
                      register={recordForm.register("name")}
                      label="Record name"
                    />
                  )}
                  <SimpleSelectFormField
                    register={recordForm.register("status")}
                    formatItemLabel={(item) => item}
                    label="Record status*"
                    items={MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS}
                    placeholder="Select status"
                    onChange={(value) => {
                      if (!value) return;
                      handleStatusChange(value);
                    }}
                  />
                </div>
                <div className="2 col-span-full grid grid-cols-subgrid">
                  {["Requested", "Received"].includes(
                    recordStatusFormValue,
                  ) && (
                    <DateFormField
                      name="requestDate"
                      register={recordForm.register("requestDate")}
                      label="Record request date*"
                    />
                  )}
                  {recordStatusFormValue === "Received" && (
                    <DateFormField
                      name="receivedDate"
                      register={recordForm.register("receivedDate")}
                      label="Record received date*"
                    />
                  )}
                  {recordStatusFormValue === "Rejected" && (
                    <DateFormField
                      name="rejectedDate"
                      register={recordForm.register("rejectedDate")}
                      label="Record rejected date*"
                    />
                  )}
                </div>
                {recordStatusFormValue === "Rejected" && (
                  <TextAreaFormField
                    register={recordForm.register("rejectedReason")}
                    label="Rejected reason*"
                    className="col-span-full"
                  />
                )}
                {selectedTreatment && !creatingFromAppointment && (
                  <fieldset
                    className="contents"
                    disabled={
                      !canCreateAppointmentConnections ||
                      (editing && !canDeleteAppointmentConnections)
                    }
                  >
                    <SelectTreatmentAppointmentsFormField
                      register={recordForm.register(
                        "medicalTreatmentAppointmentId",
                      )}
                      label="Associated treatment dates"
                      matter={matter}
                      treatment={selectedTreatment}
                    />
                  </fieldset>
                )}
                <div className="col-span-full mt-6 grid grid-cols-subgrid gap-y-2">
                  <div className="col-span-full">
                    <Separator />
                  </div>
                  <FileReferencesSection
                    editing={editing}
                    fileReferences={fileReferences}
                    matter={matter}
                    itemKey="medicalTreatmentRecordId"
                    itemId={recordInitialValues?.id ?? ""}
                    permissionKey={
                      ACTION_PERMISSION_KEYS.matterMedicalTreatmentRecord
                    }
                    maxFiles={1}
                  />
                </div>
              </fieldset>
            </div>
          </form>
        </AppModal>
      </Form>
      <ActionConfirmationModal
        open={isDeleting}
        setOpen={setIsDeleting}
        title="Delete record"
        description="Are you sure you want to delete this record? This action cannot be undone."
        buttonText="Delete"
        afterActionFn={() => {
          if (selectedRecord) {
            deleteRecord.mutate({
              matterId: matter.id,
              id: selectedRecord.id,
            });
            setOpen(false);
          }
        }}
      />
    </>
  );
};
